import React, { useState, useEffect } from 'react'
import { Spin, message } from 'antd'
import Handlebars from 'handlebars'
import HTMLReactParser from 'html-react-parser'
import {
    QuestionCircleOutlined,
    ClearOutlined,
} from '@ant-design/icons'
import { HugeRTEWrapper } from '@veritas/huge-rte-wrapper'
import styled from 'styled-components'

import { StyledButton } from '../../../Button'
import { useCaseViewContext } from '../../../../context'
import { showVariableList } from '../../../Patient/Charting/ShowVariableList'

import {
    ChartingEntryFormItem as BaseChartingEntryFormItem,
    Title,
    StyledSelect,
    CompiledChartingEntry,
    ButtonGroup,
} from '../../../Patient/Charting/ChartingEntryModal.styles'

const ChartingEntryFormItem = styled(BaseChartingEntryFormItem)`
    padding-left: 0;
    padding-right: 0;
`

export const ChartingSection = ({ onChartingNoteChange }) => {
    const { apiClasses, caseDetails, chartingPrescriptions } = useCaseViewContext()

    const [loadingTemplates, setLoadingTemplates] = useState(false)
    const [templates, setTemplates] = useState([])
    const [rawEntry, setRawEntry] = useState('')
    const [compiledEntry, setCompiledEntry] = useState('')
    const [selectedTemplate, setSelectedTemplate] = useState(null)
    const [selectedPrescription, setSelectedPrescription] = useState(null)

    const prescriptions = chartingPrescriptions ?? {}

    const queuedPrescriptionOptions = Object.values(prescriptions).map(p => ({
        value: p._id,
        label: p.name,
        key: p._id,
    }))

    const templateOptions = templates.map(t => ({
        value: t.message,
        label: t.title,
        key: t._id,
    }))

    useEffect(() => {
        if (!apiClasses?.TemplatesAPI || !caseDetails?.category?._id) return

        let cancelled = false
        setLoadingTemplates(true)
        apiClasses.TemplatesAPI.getMany({ type: 'visit', teleMedicineCategoryIds: caseDetails.category._id })
            .then(({ data }) => {
                if (!cancelled) setTemplates(Array.isArray(data?.templates) ? data.templates : [])
            })
            .catch(() => {
                if (!cancelled) message.error('Error loading charting templates')
            })
            .finally(() => {
                if (!cancelled) setLoadingTemplates(false)
            })

        return () => { cancelled = true }
    }, [apiClasses, caseDetails?.category?._id])

    const compile = (template, prescriptionId) => {
        if (!template) return ''
        try {
            const prescription = prescriptions[prescriptionId] || {}
            return Handlebars.compile(template)(prescription)
        } catch (error) {
            console.error('Template compilation error:', error)
            return template
        }
    }

    const notify = (compiled) => {
        setCompiledEntry(compiled)
        onChartingNoteChange?.(compiled)
    }

    const handleRawChange = (value) => {
        setRawEntry(value)
        notify(compile(value, selectedPrescription))
    }

    const handleTemplateSelect = (templateMessage) => {
        setSelectedTemplate(templateMessage)
        setRawEntry(templateMessage)
        notify(compile(templateMessage, selectedPrescription))
    }

    const handlePrescriptionSelect = (prescriptionId) => {
        setSelectedPrescription(prescriptionId)
        notify(compile(rawEntry, prescriptionId))
    }

    const handleClear = () => {
        setRawEntry('')
        setCompiledEntry('')
        setSelectedTemplate(null)
        setSelectedPrescription(null)
        onChartingNoteChange?.('')
    }

    return (
        <Spin spinning={loadingTemplates}>
            <ChartingEntryFormItem>
                <Title>Selected Prescription</Title>
                <StyledSelect
                    id="cc-charting-entry-prescription-select"
                    placeholder="Select a prescription to templatize"
                    options={queuedPrescriptionOptions}
                    value={selectedPrescription}
                    onSelect={handlePrescriptionSelect}
                />
            </ChartingEntryFormItem>

            <ChartingEntryFormItem>
                <Title>Template Name</Title>
                <StyledSelect
                    id="cc-charting-entry-template-select"
                    placeholder="Select a template"
                    options={templateOptions}
                    value={selectedTemplate}
                    onSelect={handleTemplateSelect}
                />
            </ChartingEntryFormItem>

            <ChartingEntryFormItem>
                <Title>Note</Title>
                <HugeRTEWrapper
                    value={rawEntry}
                    onChange={handleRawChange}
                    style={{ minHeight: '100px' }}
                />
            </ChartingEntryFormItem>

            {compiledEntry && compiledEntry !== rawEntry && (
                <ChartingEntryFormItem>
                    <Title>Rendered message</Title>
                    <CompiledChartingEntry>
                        {HTMLReactParser(compiledEntry)}
                    </CompiledChartingEntry>
                </ChartingEntryFormItem>
            )}

            <ButtonGroup>
                <StyledButton
                    id="cc-charting-entry-variables-btn"
                    type="button"
                    size="medium"
                    Icon={QuestionCircleOutlined}
                    tooltip="Template Variables"
                    onClick={showVariableList}
                />
                <StyledButton
                    id="cc-charting-entry-clear-btn"
                    type="button"
                    size="medium"
                    Icon={ClearOutlined}
                    tooltip="Clear"
                    onClick={handleClear}
                />
            </ButtonGroup>
        </Spin>
    )
}
